import React from "react";
import * as THREE from "three";
import { FloorplannerStoreContext } from "../../../../store/floorplannerStore";
import {
  JointPolygonData,
  JointFillPolygon,
  WallType,
} from "../../../../types/floorplannerTypes";
import { DEFAULT_WALL_HEIGHT } from "../../geometry";
import { resolveMaterial } from "../../materials/materialResolver";
import CatalogStandardMaterial from "../../materials/CatalogStandardMaterial";
import ExtrudedShapeMesh from "../../scene/ExtrudedShapeMesh";
import HiddenOccluderEdges from "../../scene/HiddenOccluderEdges";
import {
  applyWallTextureUVs,
  createJointShape,
  getWallHeightAtPoint,
  getWallColor,
  WallEndpointHeights,
} from "./wallGeometry";
import Wall3DMaterial from "./Wall3DMaterial";
import { resolveFillOpacity } from "../../../../utils/fillOpacity";

/**
 * Build the renderable fill islands for a joint. Prefers the boolean
 * `fillPolygons` (multiple islands, with holes); falls back to the legacy
 * single-ring `vertices` for backward compatibility.
 */
const buildJointShapes = (jointPolygon: JointPolygonData): THREE.Shape[] => {
  const shapes: THREE.Shape[] = [];

  const fromPolygon = (polygon: JointFillPolygon): THREE.Shape | null => {
    const shape = createJointShape(polygon.outer);
    if (!shape) return null;
    for (const hole of polygon.holes) {
      if (hole.length < 3) continue;
      const path = new THREE.Path();
      path.moveTo(hole[0].x, hole[0].y);
      for (let i = 1; i < hole.length; i++) {
        path.lineTo(hole[i].x, hole[i].y);
      }
      path.closePath();
      shape.holes.push(path);
    }
    return shape;
  };

  if (jointPolygon.fillPolygons && jointPolygon.fillPolygons.length > 0) {
    for (const polygon of jointPolygon.fillPolygons) {
      const shape = fromPolygon(polygon);
      if (shape) shapes.push(shape);
    }
  } else {
    const shape = createJointShape(jointPolygon.vertices);
    if (shape) shapes.push(shape);
  }

  return shapes;
};

const Joint3DMeshes: React.FC<{
  jointPolygons: JointPolygonData[];
  wallsById: Map<string, WallType>;
  wallHeightById: Map<string, number>;
  wallEndpointHeightMap: Map<string, WallEndpointHeights>;
  cameraHiddenWallIds: Set<string>;
  /** Session-only user-hidden walls; joints involving these are omitted entirely. */
  userHiddenWallIds?: Set<string>;
}> = ({
  jointPolygons,
  wallsById,
  wallHeightById,
  wallEndpointHeightMap,
  cameraHiddenWallIds,
  userHiddenWallIds,
}) => {
  const floorplannerStore = React.useContext(FloorplannerStoreContext);

  return (
    <>
      {jointPolygons.map((jointPolygon) => {
        const userHidden = jointPolygon.wallIds.some((wallId) =>
          userHiddenWallIds?.has(wallId),
        );
        if (userHidden) return null;

        const cameraHidden = jointPolygon.wallIds.some((wallId) =>
          cameraHiddenWallIds.has(wallId),
        );
        const jointHeights = jointPolygon.wallIds.map((wallId) => {
          const wall = wallsById.get(wallId);
          const endpointHeights = wallEndpointHeightMap.get(wallId);
          return wall && endpointHeights
            ? getWallHeightAtPoint(
                wall,
                endpointHeights,
                jointPolygon.jointCenter,
              )
            : wallHeightById.get(wallId) ?? DEFAULT_WALL_HEIGHT;
        });
        const height =
          jointHeights.length > 0
            ? Math.max(...jointHeights)
            : DEFAULT_WALL_HEIGHT;
        const shapes = buildJointShapes(jointPolygon);
        if (shapes.length === 0) return null;

        const primaryWall =
          (jointPolygon.primaryWallId
            ? wallsById.get(jointPolygon.primaryWallId)
            : undefined) ??
          jointPolygon.wallIds
            .map((wallId) => wallsById.get(wallId))
            .find(Boolean);
        const color = primaryWall
          ? resolveMaterial("wall", primaryWall, {
              defaultColor: getWallColor(primaryWall),
            }).color
          : resolveMaterial("joint", {
              fillColor: jointPolygon.fillColor,
            }).color;
        const configureJointTextureUVs = primaryWall
          ? (geometry: THREE.ExtrudeGeometry) => {
              applyWallTextureUVs(geometry, primaryWall);
            }
          : undefined;
        const fallbackFillOpacity = resolveFillOpacity(jointPolygon);

        if (cameraHidden) {
          return (
            <React.Fragment key={jointPolygon.id}>
              {shapes.map((shape, index) => (
                <HiddenOccluderEdges
                  key={`${jointPolygon.id}-edge-${index}`}
                  shape={shape}
                  mode="vertical"
                  height={height}
                  color={color}
                  userData={{
                    floorplannerType: "joint-edge",
                    floorplannerId: jointPolygon.id,
                  }}
                />
              ))}
            </React.Fragment>
          );
        }

        return (
          <React.Fragment key={jointPolygon.id}>
            {shapes.map((shape, index) => (
              <ExtrudedShapeMesh
                key={`${jointPolygon.id}-${index}`}
                shape={shape}
                depth={height}
                castShadow
                receiveShadow
                configureGeometry={configureJointTextureUVs}
                userData={{
                  floorplannerType: "joint",
                  floorplannerId: jointPolygon.id,
                }}
              >
                {primaryWall ? (
                  <Wall3DMaterial wall={primaryWall} color={color} />
                ) : (
                  <CatalogStandardMaterial
                    color={color}
                    defaults={floorplannerStore.threeDMaterialDefaults.wall}
                    side={THREE.DoubleSide}
                    opacity={fallbackFillOpacity}
                    transparent={fallbackFillOpacity < 1}
                    depthWrite={fallbackFillOpacity >= 1}
                  />
                )}
              </ExtrudedShapeMesh>
            ))}
          </React.Fragment>
        );
      })}
    </>
  );
};

export default Joint3DMeshes;
